import { ApiClientTokenFactory, setTokenFactory } from '@infuse/api/common';
import { setApolloTokenFactory } from '@infuse/fgw/api/graphql/client';
import { Authorizer } from './authorizer';
import { DefaultAuthorizer } from './default-authorizer';
import { PasswordPolicy } from './password-policy';

export interface LoginResult {
  /**
   * Whether the user is logged in or not. If this is false, that does not
   * necessarily mean that logging in failed. It might be that additional
   * challenges have to be completed in order to log in.
   */
  isLoggedIn: boolean;

  /**
   * If this field is set, the user is not logged in until they set a new
   * password. Call this function to set the new password and be logged in.
   */
  setNewPassword?: (password: string) => Promise<LoginResult>;

  /**
   * User has setup MFA and now needs to enter the code
   *
   * @param otp
   */
  setMFACode?: (otp: string) => Promise<LoginResult>;

  /**
   * User has setup mail MFA and now needs to enter the code
   *
   * @param otp
   */
  setMailMFACode?: (otp: string) => Promise<LoginResult>;

  /**
   * If this field is set, the user is setting up MFA
   * The totpSetupDetails contains a sharedSecret which can be
   * used to either generate a QR code or be manually entered
   * into an Authenticator app.
   */
  setupMFA?: (otp: string) => Promise<LoginResult>;
  totpSetupDetails?: { authenticatorCode: string; qrCodeUrl: string };
}

export interface User {
  fullName?: string;
  avatar?: string;
  email: string;
  authorizationHeader: string;
}

let currentAuthenticator!: Authenticator;
let currentAuthorizer!: Authorizer;

export function setCurrentAuthenticator(authenticator: Authenticator) {
  currentAuthenticator = authenticator;
}

export function useAuthenticator(): Authenticator {
  if (!currentAuthenticator) {
    throw new Error('No authenticator set, did you install the auth plugin?');
  }
  return currentAuthenticator;
}

export function setCurrentAuthorizer(authorizer: Authorizer) {
  currentAuthorizer = authorizer;
}

export function useAuthorisations() {
  return currentAuthorizer ? currentAuthorizer.isAuthorized : new DefaultAuthorizer().isAuthorized;
}

export enum UserNameType {
  EMAIL,
  TEXT
}

export abstract class Authenticator {
  constructor() {
    const factory: ApiClientTokenFactory = async () => {
      return await this.getAuthenticationHeader();
    };

    setTokenFactory(factory);
    setApolloTokenFactory(factory);
  }

  abstract getAuthenticationHeader(): Promise<string>;
  abstract getUser(): Promise<User>;
  abstract isAuthenticated(): Promise<boolean>;

  /**
   * Attempts to log the user in
   * @param username
   * @param password
   */
  abstract login(username: string, password: string): Promise<LoginResult>;

  /**
   * Provide information about the username type of this authenticator
   */
  abstract userNameType(): UserNameType;

  /**
   * Indicates whether this authenticator requires username/password credentials
   * to be entered in the UI. If false, the UI should present a provider button
   * or directly start the redirect/popup flow.
   */
  abstract requiresCredentials(): boolean;

  /**
   * Request a forgot password email
   * @param username
   */
  abstract forgotPassword(username: string): Promise<unknown>;
  abstract enableForgotPassword(): boolean;

  /**
   * Confirm password reset with new password
   * @param username
   * @param password
   * @param code
   */
  abstract forgotPasswordConfirm(username: string, password: string, code: string): Promise<unknown>;

  /**
   * Logs the current user out
   */
  abstract logout(): Promise<void>;

  abstract getPasswordPolicy(): PasswordPolicy | undefined;
}
